import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  ClipboardEvent,
  KeyboardEvent,
  ChangeEvent,
} from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'

const TAMANHO = 6

export interface OtpInputHandle {
  limparEFocar: () => void
}

interface OtpInputProps {
  onComplete: (codigo: string) => void
  disabled?: boolean
}

const OtpInput = forwardRef<OtpInputHandle, OtpInputProps>(function OtpInput(
  { onComplete, disabled = false },
  ref
) {
  const [valores, setValores] = useState<string[]>(Array(TAMANHO).fill(''))
  const valoresRef = useRef<string[]>(valores)
  valoresRef.current = valores

  const inputsRef = useRef<Array<HTMLInputElement | null>>([])

  const focar = useCallback((index: number) => {
    const alvo = inputsRef.current[index]
    if (alvo) {
      alvo.focus()
      alvo.select()
    }
  }, [])

  const aplicar = useCallback(
    (proximos: string[], focoIndex: number | null, autoSubmit = true) => {
      setValores(proximos)
      valoresRef.current = proximos
      if (focoIndex !== null) {
        requestAnimationFrame(() => focar(focoIndex))
      }
      if (autoSubmit && proximos.every((d) => d !== '')) {
        onComplete(proximos.join(''))
      }
    },
    [focar, onComplete]
  )

  const limparEFocar = useCallback(() => {
    const proximos = Array(TAMANHO).fill('')
    setValores(proximos)
    valoresRef.current = proximos
    requestAnimationFrame(() => focar(0))
  }, [focar])

  useImperativeHandle(ref, () => ({ limparEFocar }), [limparEFocar])

  const handleChange = useCallback(
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const bruto = e.target.value.replace(/\D/g, '')
      const atual = valoresRef.current

      if (bruto === '') {
        const proximos = [...atual]
        proximos[index] = ''
        aplicar(proximos, null, false)
        return
      }

      const proximos = [...atual]
      let cursor = index
      for (const d of bruto.split('')) {
        if (cursor >= TAMANHO) break
        proximos[cursor] = d
        cursor += 1
      }
      aplicar(proximos, Math.min(cursor, TAMANHO - 1))
    },
    [aplicar]
  )

  const handleKeyDown = useCallback(
    (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
      const atual = valoresRef.current

      if (e.key === 'Backspace') {
        const proximos = [...atual]
        if (proximos[index] !== '') {
          proximos[index] = ''
          aplicar(proximos, index, false)
        } else if (index > 0) {
          proximos[index - 1] = ''
          aplicar(proximos, index - 1, false)
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        focar(index - 1)
      } else if (e.key === 'ArrowRight' && index < TAMANHO - 1) {
        focar(index + 1)
      }
    },
    [aplicar, focar]
  )

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      const colado = e.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, TAMANHO)
      if (colado === '') return

      const proximos = Array(TAMANHO).fill('')
      colado.split('').forEach((d, i) => {
        proximos[i] = d
      })
      const ultimo = Math.min(colado.length, TAMANHO) - 1
      aplicar(proximos, ultimo >= 0 ? ultimo : 0)
    },
    [aplicar]
  )

  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
      {valores.map((valor, index) => (
        <TextField
          key={index}
          inputRef={(el: HTMLInputElement | null) => {
            inputsRef.current[index] = el
          }}
          value={valor}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          disabled={disabled}
          autoFocus={index === 0}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 1,
            'aria-label': `Dígito ${index + 1} de ${TAMANHO}`,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              padding: '12px 0',
            },
          }}
          sx={{ width: 48 }}
        />
      ))}
    </Box>
  )
})

export default OtpInput